import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Form, Input, Button, Space, Avatar, Upload, message, Row, Col, Divider, Tag } from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../contexts/AuthContext';
import api from '../../../../utils/api';
import './Profile.css';

function AgencyProfile() {
  const { Title, Paragraph } = Typography;
  const { currentUser, updateUser } = useAuth();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(currentUser?.profile_picture_url || '');

  const initialValues = useMemo(() => ({
    full_name: currentUser?.full_name || '',
    email: currentUser?.email || '',
    password: '',
  }), [currentUser]);

  // Fetch latest user from DB to ensure accuracy
  useEffect(() => {
    const run = async () => {
      if (!currentUser?.id) return;
      try {
        const fresh = await api.get(`/users/${currentUser.id}`);
        if (fresh) {
          updateUser({
            full_name: fresh.full_name,
            email: fresh.email,
            profile_picture_url: fresh.profile_picture_url,
          });
          form.setFieldsValue({
            full_name: fresh.full_name || '',
            email: fresh.email || '',
            password: '',
          });
          setAvatarPreview(fresh.profile_picture_url || '');
        }
      } catch (_) {
        // ignore
      } finally {
        // no-op
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const beforeUpload = async (file) => {
    try {
      const allowed = ['image/png','image/jpeg','image/jpg','image/webp'];
      if (!allowed.includes(file.type)) {
        message.error('Please upload a PNG, JPG, or WEBP image');
        return Upload.LIST_IGNORE;
      }
      const toDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const dataUrl = await toDataUrl(file);
      setAvatarPreview(dataUrl);
      // Upload immediately
      if (!currentUser?.id) {
        message.error('User not loaded');
        return Upload.LIST_IGNORE;
      }
      setUploading(true);
      const updated = await api.uploadUserProfilePicture(currentUser.id, { dataUrl });
      // Ensure role is preserved (backend already avoids role change here)
      updateUser({ profile_picture_url: updated.profile_picture_url });
      message.success('Profile picture updated');
    } catch (e) {
      message.error(e?.message || 'Failed to upload picture');
    } finally {
      setUploading(false);
    }
    return Upload.LIST_IGNORE; // prevent antd from auto-adding to list
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!currentUser?.id) throw new Error('User not loaded');
      const payload = {
        full_name: values.full_name,
        email: values.email,
      };
      if (values.password) payload.password = values.password;
      setSaving(true);
      const updated = await api.put(`/users/${currentUser.id}`, payload);
      updateUser({ full_name: updated.full_name, email: updated.email });
      message.success('Profile updated');
      form.resetFields(['password']);
    } catch (e) {
      if (e?.errorFields) return; // antd validation
      message.error(e?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agency-settings-profile">
      <Title level={3} style={{ marginBottom: 16 }}>Enterprise Profile</Title>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6} lg={4}>
            <Space direction="vertical" align="center" style={{ width: '100%' }}>
              <Avatar size={96} src={avatarPreview || currentUser?.profile_picture_url} icon={<UserOutlined />} />
              <Upload showUploadList={false} beforeUpload={beforeUpload} disabled={uploading}>
                <Button icon={<UploadOutlined />} loading={uploading} block>Change Picture</Button>
              </Upload>
            </Space>
          </Col>
          <Col xs={24} sm={16} md={18} lg={20}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Title level={4} style={{ margin: 0 }}>{currentUser?.full_name || ''}</Title>
              <Typography.Text type="secondary">{currentUser?.email}</Typography.Text>
              {currentUser?.role && (
                <div>
                  <Tag color="blue" style={{ marginTop: 4 }}>{String(currentUser.role === 'agency' ? 'enterprise' : currentUser.role).toUpperCase()}</Tag>
                </div>
              )}
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                Updating your photo will not change your role or permissions.
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Full Name" name="full_name" rules={[{ required: true, message: 'Please enter your full name' }]}>
                <Input placeholder="Full name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Security</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="New Password" name="password" extra="Leave blank to keep current password">
                <Input.Password placeholder="New password" />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button type="primary" onClick={handleSave} loading={saving}>Save Changes</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}

export default AgencyProfile;
